import React, { useState, useEffect, useMemo } from "react";
import { Form } from "@tencent/universe-ui/build/lib";
import { Button } from 'tdesign-react';
import { MathJax, MathJaxContext } from "better-react-mathjax";

const config = {
  loader: { load: ["[tex]/html"] },
  tex: {
    packages: { "[+]": ["html"] },
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"]
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"]
    ]
  }
};

function EduMarkCom({ questionInfo = [], markInfos = [], onSelect, selectRect }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [collList, setCollList] = useState([]);

  // 将markInfos按数组顺序对应到questionInfo上
  const questionsWithMark = useMemo(() => {
    if (!questionInfo || questionInfo.length === 0) return [];

    // 按数组顺序关联：questionInfo[i] 对应 markInfos[i]
    return questionInfo.map((question, index) => {
      return {
        ...question,
        markData: markInfos[index] || null
      };
    });
  }, [questionInfo, markInfos]);

  // 当前选中的题目
  const currentQuestion = questionsWithMark[currentIndex] || {};
  const currentMark = currentQuestion.markData || {};
  const { MarkItemTitle, AnswerInfos = [] } = currentMark;

  // 判断题目是否有对应的markInfo
  const hasMarkData = (index) => {
    return questionsWithMark[index]?.markData !== null;
  };

  // questionInfo首次渲染成功时，默认选中Question01
  useEffect(() => {
    if (questionsWithMark.length > 0 && hasMarkData(0)) {
      onSelect && onSelect('Question01');
    }
  }, [questionsWithMark.length > 0]);

  useEffect(() => {
    if (window.MathJax && window.MathJax.typeset) {
      window.MathJax.typeset();
    }
  }, [currentIndex]);

  // 统计错误数量（只统计有markData的题目）
  const totalQuestions = questionsWithMark.filter(q => q.markData).length;
  let wrongQuestionCount = 0;
  let wrongSubQuestionCount = 0;
  questionsWithMark.forEach(question => {
    if (!question.markData) return;
    const answers = question.markData.AnswerInfos || [];
    const hasWrong = answers.some(a => !a.IsCorrect);
    if (hasWrong) {
      wrongQuestionCount++;
    }
    answers.forEach(a => {
      if (!a.IsCorrect) {
        wrongSubQuestionCount++;
      }
    });
  });

  return (
    <div className="edu_container">
      {/* 统计提示文案 */}
      {totalQuestions > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          borderRadius: '8px',
          background: 'linear-gradient(93.26deg, #EBF2FF 0.79%, #F7FAFF 21.4%)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1C4.13 1 1 4.13 1 8C1 11.87 4.13 15 8 15C11.87 15 15 11.87 15 8C15 4.13 11.87 1 8 1ZM8 13.5C4.97 13.5 2.5 11.03 2.5 8C2.5 4.97 4.97 2.5 8 2.5C11.03 2.5 13.5 4.97 13.5 8C13.5 11.03 11.03 13.5 8 13.5Z" fill="#1890ff"/>
            <path d="M8 11C8.55228 11 9 10.5523 9 10C9 9.44772 8.55228 9 8 9C7.44772 9 7 9.44772 7 10C7 10.5523 7.44772 11 8 11Z" fill="#1890ff"/>
            <path d="M7.25 5H8.75V8H7.25V5Z" fill="#1890ff"/>
          </svg>
          <span style={{
            fontSize: '14px',
            color: '#333',
            fontWeight: 500
          }}>
            总共解析 <strong style={{ color: '#4A70ff' }}>{totalQuestions}</strong> 道大题
            {wrongQuestionCount > 0 ? (
              <>，其中有 <strong style={{ color: '#4A70ff' }}>{wrongSubQuestionCount}</strong> 道小题错误</>
            ) : (
              <>，全部正确</>
            )}
          </span>
        </div>
      )}

      {/* 题目切换按钮 - 根据questionInfo生成 */}
      <div className="edu_mark_tabs" style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {questionsWithMark.map((question, index) => {
          const hasMark = hasMarkData(index);
          return (
            <Button
              key={index}
              variant={currentIndex === index ? 'base' : 'outline'}
              theme={currentIndex === index ? 'primary' : 'default'}
              disabled={!hasMark}
              onClick={() => {
                hasMark && setCurrentIndex(index);
                onSelect && onSelect(`Question${index}1`)
              }}
              style={{
                opacity: hasMark ? 1 : 0.5,
                cursor: hasMark ? 'pointer' : 'not-allowed'
              }}
            >
              题目{index + 1}
            </Button>
          );
        })}
      </div>

      {/* 题目内容 */}
      {hasMarkData(currentIndex) && (
        <div className="edu_body_container">
          {/* 题干 */}
          {MarkItemTitle && (
            <Form.Item
              label="大题题干"
              onClick={() => onSelect && onSelect(`Question${currentIndex}1`)}
            >
              <div className="edu_form__item">
                <MathJaxContext version={3} config={config}>
                  <MathJax hideUntilTypeset={"first"}>
                    {MarkItemTitle}
                  </MathJax>
                </MathJaxContext>
              </div>
            </Form.Item>
          )}

          {/* 子题目列表 - AnswerInfos */}
          {AnswerInfos.length === 1 ? (
            // 只有一个子题目时，直接平铺数据，不展示折叠
            <>
            <Form.Item
                label="小题题干"
                onClick={() => onSelect && onSelect(`markInfo${currentIndex}_0`)}
              >
                <div className="edu_form__item">
                  <MathJaxContext version={3} config={config}>
                    <MathJax hideUntilTypeset={"first"}>
                      {AnswerInfos[0].SubQuestionTitle }
                    </MathJax>
                  </MathJaxContext>
                </div>
              </Form.Item>
              <Form.Item
                label="手写答案"
              >
                <div className="edu_form__item">
                  <MathJaxContext version={3} config={config}>
                    <MathJax hideUntilTypeset={"first"}>
                      {AnswerInfos[0].HandwriteInfo || '-'}
                    </MathJax>
                  </MathJaxContext>
                </div>
              </Form.Item>

              <Form.Item
                label="答案判断"
              >
                <div className="edu_form__item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span className={`edu-correct-icon small ${AnswerInfos[0].IsCorrect ? 'correct' : 'error'}`}></span>
                  <span>{AnswerInfos[0].IsCorrect ? '正确' : '错误'}</span>
                </div>
              </Form.Item>

              <Form.Item
                label="答案分析"
              >
                <div className="edu_form__item">
                  <MathJaxContext version={3} config={config}>
                    <MathJax hideUntilTypeset={"first"}>
                      {AnswerInfos[0].AnswerAnalysis || '-'}
                    </MathJax>
                  </MathJaxContext>
                </div>
              </Form.Item>
            </>
          ) : (
            // 多个子题目时，展示可折叠的子题目
            AnswerInfos.map((answer, answerIdx) => (
              <div
                key={answerIdx}
                style={{ marginLeft: '16px' }}
                className={`isSub ${collList.includes(`answer${answerIdx}`) ? 'isHide' : 'isActive'} ${selectRect === `markInfo${currentIndex}_${answerIdx}` ? 'isChecked' : ''} edu_container__item`}
              >
                <div className="edu_title_container" 
                onClick={() => {
                  onSelect && onSelect(`markInfo${currentIndex}_${answerIdx}`)
                }}>
                  <span className="edu_title__tips"></span>
                  <span className="edu_title__text">子题目{answerIdx + 1}</span>
                  <span className="edu_title__collaps" onClick={(event) => {
                    event.stopPropagation();
                    let _collList = [...collList];
                    const key = `answer${answerIdx}`;
                    if (_collList.includes(key)) {
                      _collList.splice(_collList.indexOf(key), 1);
                    } else {
                      _collList.push(key);
                    }
                    setCollList(_collList);
                  }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.77971 9L7.00005 6.33766L4.22039 9L3.0001 7.83117L7.00005 4L11 7.83117L9.77971 9Z" fill="#32394B" />
                    </svg>
                  </span>
                </div>
                <div className="edu_body_container">
                  <Form.Item
                      label="小题题干"
                      onClick={() => {
                        onSelect && onSelect(`markInfo${currentIndex}_${answerIdx}`)
                      }}
                    >
                      <div className="edu_form__item">
                        <MathJaxContext version={3} config={config}>
                          <MathJax hideUntilTypeset={"first"}>
                            {answer.SubQuestionTitle }
                          </MathJax>
                        </MathJaxContext>
                      </div>
                    </Form.Item>
                  <Form.Item
                    label="手写答案"
                  >
                    <div className="edu_form__item">
                      <MathJaxContext version={3} config={config}>
                        <MathJax hideUntilTypeset={"first"}>
                          {answer.HandwriteInfo}
                        </MathJax>
                      </MathJaxContext>
                    </div>
                  </Form.Item>

                  <Form.Item
                    label="答案判断"
                  >
                    <div className="edu_form__item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span className={`edu-correct-icon small ${answer.IsCorrect ? 'correct' : 'error'}`}></span>
                      <span>{answer.IsCorrect ? '正确' : '错误'}</span>
                    </div>
                  </Form.Item>

                  <Form.Item
                    label="答案分析"
                  >
                    <div className="edu_form__item">
                      <MathJaxContext version={3} config={config}>
                        <MathJax hideUntilTypeset={"first"}>
                          {answer.AnswerAnalysis}
                        </MathJax>
                      </MathJaxContext>
                    </div>
                  </Form.Item>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default EduMarkCom;
